import { AttentionDot, Avatar, Chip, ParsleyLockup, ResponsiveDialog } from '@parsleyai/ui'
import { CircleHelp, Gift, LogOut, Newspaper } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import InviteFriendDialog from '@/components/invite/InviteFriendDialog'
import { canInvite, deriveInviteAllowance } from '@/components/invite/inviteQuota'
import ThemeToggle from '@/components/ThemeToggle'
import { useWhatsNewIndicator } from '@/pages/whatsNew/useWhatsNewIndicator'
import { useAuth, useProfile } from '@/store'

interface Props {
  open: boolean
  onClose: () => void
}

// The mobile account menu — a bottom sheet built on the shared `responsive-dialog` primitive
// (drag-to-dismiss + spring). Rows sit within the sheet's default padding.
export default function MobileAvatarSheet({ open, onClose }: Props) {
  const { signOut, user } = useAuth()
  const { capabilities, inviteQuota } = useProfile()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [inviteOpen, setInviteOpen] = useState(false)
  const { unseen: whatsNewUnseen } = useWhatsNewIndicator()

  const showInvite = canInvite(capabilities)
  const invitesLeft = deriveInviteAllowance(capabilities, inviteQuota, Date.now()).remaining

  // Tagged app version, matching the desktop footer (Footer.tsx).
  const version = `v${import.meta.env.VITE_APP_VERSION}`

  const handleSignOut = () => {
    signOut()
    onClose()
  }

  return (
    <>
      <ResponsiveDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) onClose()
        }}
        presentation="sheet"
        hideClose
        aria-label={t('nav.account')}>
        {/* User info */}
        <div className="flex items-center gap-3 px-3 py-4">
          <Avatar src={user?.photoURL} name={user?.displayName} size="md" />
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground text-sm">{user?.displayName || t('nav.account')}</p>
            {user?.email && <p className="truncate text-muted-foreground text-xs">{user.email}</p>}
          </div>
        </div>

        {/* Separators poke ~4px past the rows (via -mx-1) so they read like the desktop menu. */}
        <div className="-mx-1 h-px bg-border" />

        {/* Actions */}
        <div className="py-1.5">
          {showInvite && (
            <>
              <button
                type="button"
                onClick={() => {
                  onClose()
                  setInviteOpen(true)
                }}
                className="flex min-h-[44px] w-full items-center gap-3 rounded-xl bg-brand-green-50/55 px-3 py-3 text-foreground text-sm transition-colors hover:bg-brand-green-50 dark:bg-gray-800/40 dark:hover:bg-gray-800/70">
                <span className="flex size-[30px] flex-shrink-0 items-center justify-center rounded-[9px] bg-brand-green-600 text-white shadow-[0_1px_2px_oklch(var(--brand-green-900)/0.25)]">
                  <Gift className="size-[17px]" />
                </span>
                <span className="flex-1 text-left">{t('nav.userMenu.invite')}</span>
                <Chip variant="meta" className="py-1 font-light font-mono text-2xs leading-none">
                  {t('invite.menuBadge', { count: invitesLeft })}
                </Chip>
              </button>
              {/* Separator under the invite item, matching the desktop menu. */}
              <div className="-mx-1 my-1.5 h-px bg-border" />
            </>
          )}

          {/* Help */}
          <button
            type="button"
            onClick={() => {
              navigate('/settings/help')
              onClose()
            }}
            className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-3 text-foreground text-sm transition-colors hover:bg-muted/60">
            <CircleHelp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            {t('nav.userMenu.help')}
          </button>

          {/* What's new — the mobile counterpart to the desktop footer's link. This sheet IS the
              mobile footer (PageLayout gates the real one behind `hidden md:block`), so without
              an entry here the release notes would be desktop-only. */}
          <button
            type="button"
            onClick={() => {
              navigate('/whats-new')
              onClose()
            }}
            className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-3 text-foreground text-sm transition-colors hover:bg-muted/60">
            <Newspaper className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            {t('footer.whatsNew')}
            {whatsNewUnseen && <AttentionDot label={t('whatsNew.unseenLabel')} />}
          </button>

          {/* Theme toggle */}
          <div className="flex min-h-[44px] items-center py-2">
            <ThemeToggle />
          </div>
        </div>

        <div className="-mx-1 h-px bg-border" />

        {/* Sign out — centered + neutral, matching the desktop menu. */}
        <div className="py-1.5">
          <button
            type="button"
            onClick={handleSignOut}
            className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-3 py-3 text-foreground text-sm transition-colors hover:bg-muted/60">
            <LogOut className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            {t('nav.signOut')}
          </button>
        </div>

        <div className="-mx-1 h-px bg-border" />

        {/* Footer — mirrors the desktop App footer (Footer.tsx) on the sheet's own surface:
            brand wordmark, live "Private beta" pill, legal links, and a mono version line. */}
        <div className="px-3 pt-4 pb-2">
          {/* Brand + live status pill */}
          <div className="mb-4 flex items-center gap-2.5">
            <ParsleyLockup variant="bare" size="sm" className="text-brand-green-700 dark:text-brand-green-300" />
            <Chip variant="meta" tone="accent" className="ml-auto font-mono text-xs tracking-[0.02em]">
              {t('footer.betaPill')}
            </Chip>
          </div>

          {/* Legal links — all three point at the in-app pages here, where the desktop footer sends
              you out to the marketing site. */}
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2" aria-label={t('footer.legalLabel')}>
            <Link to="/privacy" onClick={onClose} className="text-muted-foreground text-sm transition-colors hover:text-foreground">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms" onClick={onClose} className="text-muted-foreground text-sm transition-colors hover:text-foreground">
              {t('footer.terms')}
            </Link>
            <Link to="/cookies" onClick={onClose} className="text-muted-foreground text-sm transition-colors hover:text-foreground">
              {t('footer.cookies')}
            </Link>
          </nav>

          {/* Copyright + version — mono with a mid-dot separator, like the desktop footer bar. */}
          <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 border-border border-t pt-4 font-mono text-xs text-muted-foreground/60 tracking-[0.02em]">
            <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
            <span className="opacity-50">·</span>
            <span>{t('common.version', { version })}</span>
          </p>
        </div>
      </ResponsiveDialog>

      {showInvite && <InviteFriendDialog open={inviteOpen} onOpenChange={setInviteOpen} />}
    </>
  )
}
